import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { ClientService } from '../../core/services/client.service';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { NgIf, NgFor } from '@angular/common';
import { UploadAvatarImageComponent } from '../../shared-with-client/upload-avatar-image/upload-avatar-image.component';
import { LoaderListPlaceholderComponent } from '../../shared-with-client/loader-list-placeholder/loader-list-placeholder.component';

@Component({
    selector: 'app-client-providers',
    templateUrl: './client-providers.component.html',
    styleUrls: ['./client-providers.component.scss'],
    imports: [
        NgIf,
        NgFor,
        UploadAvatarImageComponent,
        LoaderListPlaceholderComponent,
        TranslatePipe
    ],
    standalone: true
})
export class ClientProvidersComponent implements OnInit, OnDestroy {

  @Input() store;
  @Input() services;
  @Input() bypassChooseProviders;
  @Input() mandatoryChooseProviders = false;
  @Output() saved = new EventEmitter;

  public getStoreProvidersSubscription: Subscription;

  public servicesAndProvidersArray = [];

  public multiBookingMode = false;

  private anyProvider = {
    name: this.translate.instant('CLIENT.clientProviders.withoutPreference'),
    id: null
  };

  public providers;
  public loading = true;

  constructor(private clientService: ClientService, private translate: TranslateService) { }

  get servicesWithoutProviders(): any {
    return this.servicesAndProvidersArray.filter(service => !service.selectedProvider);
  }

  ngOnInit() {
    this.getStoreProvidersSubscription = this.clientService
      .getStoreProviders(this.store.id, this.services, !this.store.multipleBookings)
      .subscribe((res) => {
        // merchant does not want client to be able to choose providers
        if (this.bypassChooseProviders) {
          this.saved.emit([Object.assign(this.anyProvider, { auto: true })]);
          return;
        }

        if (!this.store.multipleBookings || this.services.length === 1) {
          this.setupSingleBookings(res);
        } else {
          this.multiBookingMode = true;
          this.setupMultiBookings(res);
        }
      });
  }

  ngOnDestroy() {
    if (this.getStoreProvidersSubscription) {
      this.getStoreProvidersSubscription.unsubscribe();
    }
  }

  setupSingleBookings(providers) {
    this.providers = providers;

    if (providers.length <= 1) {
      this.saved.emit([Object.assign({ name: providers[0]?.name, id: providers[0]?.id }, { auto: true })]);
    }

    // merchant does not want client to be able to choose without providers
    if (!this.mandatoryChooseProviders) {
      this.providers.unshift(this.anyProvider);
    }

    this.loading = false;
  }

  setupMultiBookings(providers) {
    let hasPreselectedProviders = false;

    // if store only has one provider assign it to all services
    if (providers.length <= 1) {
      const toSave = [];
      this.services.forEach(service => toSave.push(Object.assign({ name: providers[0]?.name, id: providers[0]?.id }, { auto: true })));
      this.saved.emit(toSave);
      return;
    }

    this.servicesAndProvidersArray = this.services.map(service => {
      service.providers = providers.filter(provider => provider.services.some(s => s.id === service.id));

      // check if service only has non or only one available provider and auto select
      const noProvider = service.providers.length === 0;
      if (noProvider) {
        service.providers.unshift(this.anyProvider);
        service.selectedProvider = this.anyProvider;
        hasPreselectedProviders = true;
        return service;
      }
      const onlyOneProvider = service.providers.length === 1;
      if (onlyOneProvider) {
        service.selectedProvider = service.providers[0];
        hasPreselectedProviders = true;
        return service;
      }

      // merchant does not want client to be able to choose without providers
      if (!this.mandatoryChooseProviders && !onlyOneProvider) {
        service.providers.unshift(this.anyProvider);
      }

      return service;
    });

    // if no services left to assign after auto assign move on
    const allAutoSelected = this.servicesAndProvidersArray.filter(v => !v.selectedProvider)?.length === 0;
    if (allAutoSelected) {
      // BUG going back from choose date
      const hasDateRequestCache = this.clientService.daySearchResultCache.getValue()?.storeId ? true : false;
      if (!hasDateRequestCache) {
        this.saveMultiSelection(true);
      }
    }

    // sort services to put services with unselected provider on the bottom
    if (hasPreselectedProviders) {
      this.servicesAndProvidersArray.sort((a, b) => {
        const aWeight = a.selectedProvider ? 1 : 0;
        const bWeight = b.selectedProvider ? 1 : 0;
        return aWeight - bWeight;
      });
    }

    this.loading = false;
  }

  selectProvider(provider) {
    this.saved.emit([provider]);
  }

  saveMultiSelection(auto = false) {
    const toSave = [];
    this.servicesAndProvidersArray.forEach(service => {
      if (auto) {
        toSave.push({...service.selectedProvider, auto});
      } else {
        toSave.push(service.selectedProvider);
      }
    });
    this.saved.emit(toSave);
  }
}
